import type {
  ConversationInteractiveButtonItem,
  ConversationInteractiveButtons,
  ConversationInteractiveList,
  ConversationInteractiveListRow,
  ConversationInteractiveListSection,
} from '../../conversations/types/Conversation';
import type {
  AutomationActionFailureMode,
  AutomationCondition,
  AutomationConditionLogic,
  AutomationConditionRequest,
} from '../../automations/types/AutomationCondition';
import {
  flattenConditionKeywords,
  normalizeActionFailureMode,
  normalizeAutomationConditions,
  normalizeConditionLogic,
  normalizeKeywordMatchMode,
} from '../../automations/types/AutomationCondition';

export type DmAutomationChannel =
  | 'whatsapp'
  | 'instagram'
  | 'telegram'
  | 'messenger'
  | 'tiktok'
  | 'webchat';
export type DmAutomationMatchMode = 'equals' | 'contains' | 'starts_with';
export type DmAutomationMessageTiming = 'any' | 'first_ever' | 'first_today';
export type DmAutomationActionType = 'dm_reply';
export type DmAutomationBodySource = 'template' | 'ai' | 'interactive';
export type DmAutomationInteractiveMode = 'list' | 'buttons';

export type DmAutomationAction = {
  type: DmAutomationActionType;
  bodySource: DmAutomationBodySource;
  templateText?: string | null;
  fallbackTemplateText?: string | null;
  instruction?: string | null;
  promptUseCase?: string | null;
  interactiveMode?: DmAutomationInteractiveMode | null;
  interactiveList?: ConversationInteractiveList | null;
  interactiveButtons?: ConversationInteractiveButtons | null;
};

export type DmAutomationRule = {
  id: string;
  name: string;
  channel: DmAutomationChannel;
  channelAccountId?: string | null;
  isEnabled: boolean;
  priority: number;
  matchMode: DmAutomationMatchMode;
  messageTiming: DmAutomationMessageTiming;
  keywords: string[];
  conditionLogic: AutomationConditionLogic;
  conditions: AutomationCondition[];
  onActionFailure: AutomationActionFailureMode;
  actions: DmAutomationAction[];
  /** Max interactive reply buttons for this rule's channel. `null` = no limit. */
  interactiveButtonMaxCount?: number | null;
  createdAt: string;
  updatedAt: string;
};

export type DmAutomationListQuery = {
  channel?: DmAutomationChannel | '';
  isEnabled?: boolean | '';
  page?: number;
  pageSize?: number;
};

export type DmAutomationActionRequest = {
  type: DmAutomationActionType;
  bodySource: DmAutomationBodySource;
  templateText?: string | null;
  fallbackTemplateText?: string | null;
  instruction?: string | null;
  promptUseCase?: string | null;
  interactiveList?: ConversationInteractiveList | null;
  interactiveButtons?: ConversationInteractiveButtons | null;
};

export type CreateDmAutomationRuleRequest = {
  name: string;
  channel: DmAutomationChannel;
  channelAccountId?: string | null;
  isEnabled?: boolean;
  priority?: number;
  matchMode?: DmAutomationMatchMode;
  messageTiming?: DmAutomationMessageTiming;
  keywords?: string[];
  conditionLogic?: AutomationConditionLogic;
  conditions?: AutomationConditionRequest[];
  onActionFailure?: AutomationActionFailureMode;
  actions: DmAutomationActionRequest[];
};

export type UpdateDmAutomationRuleRequest = CreateDmAutomationRuleRequest;

export type PagedDmAutomationResult = {
  items: DmAutomationRule[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasNext: boolean;
};

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeOptionalText = (value: unknown): string | undefined => {
  const normalized = asOptionalString(value);
  return normalized ?? undefined;
};

const normalizeInteractiveButtonMaxCount = (value: unknown): number | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const createInteractiveDraftId = (prefix: 'row' | 'btn'): string =>
  `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

const normalizeChannel = (value: unknown): DmAutomationChannel => {
  const normalized = String(value ?? '').trim().toLowerCase().replace('-', '_');
  if (normalized === 'whatsapp' || normalized === 'whats_app') {
    return 'whatsapp';
  }
  if (normalized === 'telegram') {
    return 'telegram';
  }
  if (normalized === 'messenger' || normalized === 'facebook' || normalized === 'facebook_messenger') {
    return 'messenger';
  }
  if (normalized === 'tiktok' || normalized === 'tik_tok') {
    return 'tiktok';
  }
  if (normalized === 'webchat' || normalized === 'web_chat' || normalized === 'widget') {
    return 'webchat';
  }
  return 'instagram';
};

const normalizeMatchMode = (value: unknown): DmAutomationMatchMode => {
  const normalized = String(value ?? '').trim().toLowerCase().replace('-', '_');
  if (normalized === 'contains') {
    return 'contains';
  }
  if (normalized === 'starts_with' || normalized === 'startswith') {
    return 'starts_with';
  }
  return 'equals';
};

export const normalizeMessageTiming = (value: unknown): DmAutomationMessageTiming => {
  const normalized = String(value ?? '').trim().toLowerCase().replace('-', '_');
  if (normalized === 'first_ever' || normalized === 'firstever') {
    return 'first_ever';
  }
  if (normalized === 'first_today' || normalized === 'firsttoday' || normalized === 'first_of_day') {
    return 'first_today';
  }
  return 'any';
};

const normalizeBodySource = (
  value: unknown,
  interactiveList?: ConversationInteractiveList | null,
  interactiveButtons?: ConversationInteractiveButtons | null,
): DmAutomationBodySource => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'ai') {
    return 'ai';
  }
  if (normalized === 'interactive' || interactiveList || interactiveButtons) {
    return 'interactive';
  }
  return 'template';
};

const normalizeInteractiveRow = (value: any): ConversationInteractiveListRow => ({
  id: asString(value?.id).trim(),
  title: asString(value?.title).trim(),
  description: normalizeOptionalText(value?.description),
});

const normalizeInteractiveSection = (value: any): ConversationInteractiveListSection => ({
  title: normalizeOptionalText(value?.title),
  rows: Array.isArray(value?.rows)
    ? value.rows.map(normalizeInteractiveRow).filter((row: ConversationInteractiveListRow) => row.id && row.title)
    : [],
});

export const normalizeInteractiveList = (value: any): ConversationInteractiveList | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const bodyText = asOptionalString(value?.bodyText);
  const buttonText = asOptionalString(value?.buttonText);
  const sections = Array.isArray(value?.sections)
    ? value.sections
      .map(normalizeInteractiveSection)
      .filter((section: ConversationInteractiveListSection) => section.rows.length > 0)
    : [];

  if (!bodyText || !buttonText || sections.length === 0) {
    return null;
  }

  return {
    headerText: normalizeOptionalText(value?.headerText),
    bodyText,
    footerText: normalizeOptionalText(value?.footerText),
    buttonText,
    sections,
  };
};

const normalizeInteractiveButtonItem = (value: any): ConversationInteractiveButtonItem => ({
  id: asString(value?.id).trim(),
  title: asString(value?.title).trim(),
});

export const normalizeInteractiveButtons = (value: any): ConversationInteractiveButtons | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const bodyText = asOptionalString(value?.bodyText);
  const buttons = Array.isArray(value?.buttons)
    ? value.buttons
      .map(normalizeInteractiveButtonItem)
      .filter((button: ConversationInteractiveButtonItem) => button.title)
    : [];

  if (!bodyText || buttons.length === 0) {
    return null;
  }

  return {
    headerText: normalizeOptionalText(value?.headerText),
    bodyText,
    footerText: normalizeOptionalText(value?.footerText),
    buttons,
  };
};

export const emptyInteractiveRow = (): ConversationInteractiveListRow => ({
  id: createInteractiveDraftId('row'),
  title: '',
  description: '',
});

export const emptyInteractiveSection = (): ConversationInteractiveListSection => ({
  title: '',
  rows: [emptyInteractiveRow()],
});

export const emptyInteractiveList = (): ConversationInteractiveList => ({
  headerText: '',
  bodyText: '',
  footerText: '',
  buttonText: 'Open list',
  sections: [emptyInteractiveSection()],
});

export const emptyInteractiveButton = (): ConversationInteractiveButtonItem => ({
  id: createInteractiveDraftId('btn'),
  title: '',
});

export const emptyInteractiveButtons = (): ConversationInteractiveButtons => ({
  headerText: '',
  bodyText: '',
  footerText: '',
  buttons: [emptyInteractiveButton()],
});

export const toInteractiveListDraft = (
  value?: ConversationInteractiveList | null,
): ConversationInteractiveList => {
  if (!value) {
    return emptyInteractiveList();
  }
  return {
    headerText: value.headerText ?? '',
    bodyText: value.bodyText ?? '',
    footerText: value.footerText ?? '',
    buttonText: value.buttonText ?? 'Open list',
    sections: (value.sections?.length ? value.sections : [emptyInteractiveSection()]).map((section) => ({
      title: section.title ?? '',
      rows: (section.rows?.length ? section.rows : [emptyInteractiveRow()]).map((row) => ({
        id: row.id || createInteractiveDraftId('row'),
        title: row.title ?? '',
        description: row.description ?? '',
      })),
    })),
  };
};

export const toInteractiveButtonsDraft = (
  value?: ConversationInteractiveButtons | null,
): ConversationInteractiveButtons => {
  if (!value) {
    return emptyInteractiveButtons();
  }
  return {
    headerText: value.headerText ?? '',
    bodyText: value.bodyText ?? '',
    footerText: value.footerText ?? '',
    buttons: (value.buttons?.length ? value.buttons : [emptyInteractiveButton()]).map((button) => ({
      id: button.id || createInteractiveDraftId('btn'),
      title: button.title ?? '',
    })),
  };
};

const serializeInteractiveListForRequest = (
  draft: ConversationInteractiveList,
): ConversationInteractiveList => ({
  headerText: draft.headerText?.trim() || undefined,
  bodyText: draft.bodyText.trim(),
  footerText: draft.footerText?.trim() || undefined,
  buttonText: draft.buttonText.trim(),
  sections: draft.sections
    .map((section) => ({
      title: section.title?.trim() || undefined,
      rows: section.rows
        .filter((row) => row.id.trim() && row.title.trim())
        .map((row) => ({
          id: row.id.trim(),
          title: row.title.trim(),
          description: row.description?.trim() || undefined,
        })),
    }))
    .filter((section) => section.rows.length > 0),
});

const serializeInteractiveButtonsForRequest = (
  draft: ConversationInteractiveButtons,
): ConversationInteractiveButtons => ({
  headerText: draft.headerText?.trim() || undefined,
  bodyText: draft.bodyText.trim(),
  footerText: draft.footerText?.trim() || undefined,
  buttons: draft.buttons
    .filter((button) => button.title.trim())
    .slice(0, 3)
    .map((button) => ({
      id: button.id.trim() || createInteractiveDraftId('btn'),
      title: button.title.trim(),
    })),
});

export const validateInteractiveAction = (action: DmAutomationAction): string | null => {
  if (action.bodySource !== 'interactive') {
    return null;
  }

  const mode: DmAutomationInteractiveMode =
    action.interactiveMode
    ?? (action.interactiveButtons && !action.interactiveList ? 'buttons' : 'list');

  if (mode === 'buttons') {
    const draft = toInteractiveButtonsDraft(action.interactiveButtons);
    if (!draft.bodyText.trim()) {
      return 'interactiveButtonsBodyRequired';
    }
    if (!draft.buttons.some((button) => button.title.trim())) {
      return 'interactiveButtonsRequired';
    }
    return null;
  }

  const draft = toInteractiveListDraft(action.interactiveList);
  if (!draft.bodyText.trim()) {
    return 'interactiveListBodyRequired';
  }
  if (!draft.buttonText.trim()) {
    return 'interactiveListButtonRequired';
  }
  if (draft.sections.length === 0) {
    return 'interactiveListSectionRequired';
  }
  if (!draft.sections.some((section) => section.rows.some((row) => row.title.trim()))) {
    return 'interactiveListRowRequired';
  }
  return null;
};

export const toDmAutomationActionRequest = (
  action: DmAutomationAction,
): DmAutomationActionRequest => {
  if (action.bodySource === 'interactive') {
    const mode: DmAutomationInteractiveMode =
      action.interactiveMode
      ?? (action.interactiveButtons && !action.interactiveList ? 'buttons' : 'list');
    const interactiveList = mode === 'list'
      ? serializeInteractiveListForRequest(toInteractiveListDraft(action.interactiveList))
      : null;
    const interactiveButtons = mode === 'buttons'
      ? serializeInteractiveButtonsForRequest(toInteractiveButtonsDraft(action.interactiveButtons))
      : null;

    return {
      type: 'dm_reply',
      bodySource: 'interactive',
      templateText: null,
      fallbackTemplateText: null,
      instruction: null,
      promptUseCase: null,
      interactiveList,
      interactiveButtons,
    };
  }

  return {
    type: 'dm_reply',
    bodySource: action.bodySource === 'ai' ? 'ai' : 'template',
    templateText: action.bodySource === 'template' ? (action.templateText?.trim() || null) : null,
    fallbackTemplateText: action.bodySource === 'ai' ? (action.fallbackTemplateText?.trim() || null) : null,
    instruction: action.bodySource === 'ai' ? (action.instruction?.trim() || null) : null,
    promptUseCase: action.promptUseCase?.trim() || null,
    interactiveList: null,
    interactiveButtons: null,
  };
};

export const summarizeInteractiveAction = (action: DmAutomationAction): {
  mode: DmAutomationInteractiveMode;
  optionCount: number;
} | null => {
  if (action.bodySource !== 'interactive') {
    return null;
  }

  if (action.interactiveButtons && (!action.interactiveList || action.interactiveMode === 'buttons')) {
    return {
      mode: 'buttons',
      optionCount: action.interactiveButtons.buttons.length,
    };
  }

  if (action.interactiveList) {
    return {
      mode: 'list',
      optionCount: action.interactiveList.sections.reduce((total, section) => total + section.rows.length, 0),
    };
  }

  return {
    mode: action.interactiveMode === 'buttons' ? 'buttons' : 'list',
    optionCount: 0,
  };
};

export const normalizeDmAutomationAction = (value: any): DmAutomationAction => {
  const interactiveList = normalizeInteractiveList(value?.interactiveList);
  const interactiveButtons = normalizeInteractiveButtons(value?.interactiveButtons);
  const bodySource = normalizeBodySource(value?.bodySource, interactiveList, interactiveButtons);

  if (bodySource === 'interactive') {
    const interactiveMode: DmAutomationInteractiveMode =
      interactiveButtons && !interactiveList
        ? 'buttons'
        : interactiveList
          ? 'list'
          : String(value?.interactiveMode ?? '').trim().toLowerCase() === 'buttons'
            ? 'buttons'
            : 'list';

    return {
      type: 'dm_reply',
      bodySource: 'interactive',
      templateText: null,
      fallbackTemplateText: null,
      instruction: null,
      promptUseCase: null,
      interactiveMode,
      interactiveList: toInteractiveListDraft(interactiveList),
      interactiveButtons: toInteractiveButtonsDraft(interactiveButtons),
    };
  }

  return {
    type: 'dm_reply',
    bodySource,
    templateText: asOptionalString(value?.templateText),
    fallbackTemplateText: asOptionalString(value?.fallbackTemplateText),
    instruction: asOptionalString(value?.instruction),
    promptUseCase: asOptionalString(value?.promptUseCase),
    interactiveMode: null,
    interactiveList: null,
    interactiveButtons: null,
  };
};

export const normalizeDmAutomationRule = (value: any): DmAutomationRule => ({
  id: asString(value?.id),
  name: asString(value?.name),
  channel: normalizeChannel(value?.channel),
  channelAccountId: asOptionalString(value?.channelAccountId),
  isEnabled: Boolean(value?.isEnabled),
  priority: Number(value?.priority ?? 100),
  matchMode: normalizeMatchMode(value?.matchMode),
  messageTiming: normalizeMessageTiming(value?.messageTiming ?? value?.MessageTiming),
  keywords: Array.isArray(value?.keywords)
    ? value.keywords.map((item: unknown) => asString(item).trim()).filter(Boolean)
    : flattenConditionKeywords(normalizeAutomationConditions(value?.conditions)),
  conditionLogic: normalizeConditionLogic(value?.conditionLogic),
  onActionFailure: normalizeActionFailureMode(value?.onActionFailure),
  conditions: normalizeAutomationConditions(
    value?.conditions,
    Array.isArray(value?.keywords)
      ? value.keywords.map((item: unknown) => asString(item).trim()).filter(Boolean)
      : [],
    normalizeKeywordMatchMode(value?.matchMode),
  ),
  actions: Array.isArray(value?.actions)
    ? value.actions.map(normalizeDmAutomationAction)
    : [],
  interactiveButtonMaxCount: normalizeInteractiveButtonMaxCount(value?.interactiveButtonMaxCount),
  createdAt: asString(value?.createdAt),
  updatedAt: asString(value?.updatedAt),
});

export const normalizePagedDmAutomations = (value: any): PagedDmAutomationResult => ({
  items: Array.isArray(value?.items) ? value.items.map(normalizeDmAutomationRule) : [],
  page: Number(value?.page ?? 1),
  pageSize: Number(value?.pageSize ?? 25),
  totalCount: Number(value?.totalCount ?? value?.total ?? 0),
  hasNext: Boolean(value?.hasNext),
});

export const emptyAction = (): DmAutomationAction => ({
  type: 'dm_reply',
  bodySource: 'template',
  templateText: '',
  fallbackTemplateText: '',
  instruction: '',
  promptUseCase: null,
  interactiveMode: null,
  interactiveList: null,
  interactiveButtons: null,
});
